import {User} from '../../../../../backend/interfaces/user';
import {ActivitiesGroup} from '../../../../../backend/interfaces/activities-group';
import {ActivitiesSector} from '../../../../../backend/interfaces/activities-sector';
import {ActivityType} from '../../../../../backend/interfaces/activity-type';
import {CarePlan} from '../../../../../backend/interfaces/care-plan';
import {CarePlanActivitiesGroup} from '../../../../../backend/interfaces/care-plan-activities-group';
import {CarePlanConfiguration} from '../../../../../backend/interfaces/care-plan-configuration';
import {EntityActivitiesRepository} from '../../../../../repositories/entity-activities';
import {EntityUserCarePlansRepository} from '../../../../../repositories/entity-user-care-plans';
import {processAdditionalActivityTypeData} from './export-care-plan-get-report-additional-data';
import {
  ExportActivityTypeData,
  ExportCarePlanReportData,
  ExportGroupDetailsData,
} from './export-care-plan-types';

/**
 * Fetches all necessary data for exporting a care plan.
 */
export async function exportCarePlanGetReportData({
  entityId,
  senior,
  activitiesSectorId,
  activitiesGroupId,
}: {
  entityId?: string;
  senior?: User;
  activitiesSectorId?: string;
  activitiesGroupId?: string;
}): Promise<ExportCarePlanReportData | null> {
  if (!entityId || !senior?.id || !activitiesSectorId || !activitiesGroupId) {
    return null;
  }

  // 0. Fetch all base data
  const baseData = await fetchCarePlanBaseData(
    entityId,
    senior.id,
    activitiesSectorId,
    activitiesGroupId
  );

  // 1. Process user Activity Types
  const exportActivityTypesData = await processUserActivityTypesData(
    baseData,
    entityId,
    activitiesSectorId,
    activitiesGroupId
  );

  // 2. Process Group Details
  const userGroupDetailsData = processUserGroupDetailsData(baseData);

  // 3. Done
  return {
    entityData: baseData.entityData,
    sectorData: baseData.sectorData,
    groupData: baseData.groupData,
    groupDetailsData: baseData.groupDetailsData,
    groupActivitiesTypesData: baseData.groupActivitiesTypesData,
    userCarePlanData: baseData.userCarePlanData,
    userGroupData: baseData.userGroupData,
    userActivityTypesData: Array.from(exportActivityTypesData.values()),
    userGroupDetailsData,
  };
}

// ------------------------------------------------------------------------------------
// ------------------------------------------------------------------------------------
// ------------------------------------------------------------------------------------

// 1. Fetch all base data
export type BaseReportData = {
  entityData: {id: string};
  sectorData: ActivitiesSector;
  groupData: ActivitiesGroup;
  groupDetailsData: CarePlanConfiguration[];
  groupActivitiesTypesData: ActivityType[];
  userCarePlanData: CarePlan;
  userGroupData: CarePlanActivitiesGroup;
};

async function fetchCarePlanBaseData(
  entityId: string,
  seniorId: string,
  activitiesSectorId: string,
  activitiesGroupId: string
): Promise<BaseReportData> {
  const [sectorData, groupData, groupDetailsData, groupActivitiesTypesData, userCarePlanData] =
    await Promise.all([
      // Sector information (ABVD, AIVD, etc.)
      EntityActivitiesRepository.instance
        .getAllSectors(entityId)
        .then((sectors) => sectors.find((s) => s.id === activitiesSectorId)),
      // Group infomation (Medication, Nutrition, Sleep, Hygiene, etc.)
      EntityActivitiesRepository.instance.getGroup(entityId, activitiesSectorId, activitiesGroupId),
      // Group Details (observations, diet, autonomy level, etc.)
      EntityActivitiesRepository.instance.getGroupCarePlanConfigurations(
        entityId,
        activitiesSectorId,
        activitiesGroupId
      ),
      // Group Activities Types (Breakfast, Dinner, Medication, etc.)
      EntityActivitiesRepository.instance.getAllTypes(
        entityId,
        activitiesSectorId,
        activitiesGroupId
      ),
      // User care plan
      EntityUserCarePlansRepository.instance.getCarePlan(entityId, seniorId, activitiesSectorId),
    ]);

  const userGroupData = userCarePlanData?.activitiesGroups?.find(
    (g) => g.activitiesGroupId === activitiesGroupId
  );

  const entityData = {
    id: entityId,
  };

  if (!sectorData) {
    throw new Error('Sector data not found');
  }

  if (!userCarePlanData) {
    throw new Error('User care plan not found');
  }
  if (!userGroupData) {
    throw new Error('User care plan group not found');
  }
  if (!groupData) {
    throw new Error('Group data not found');
  }
  if (!groupDetailsData) {
    throw new Error('Group config data not found');
  }

  return {
    entityData,
    sectorData,
    groupData,
    groupDetailsData,
    groupActivitiesTypesData,
    userCarePlanData,
    userGroupData,
  };
}

// 2. Process user Activity Types
async function processUserActivityTypesData(
  baseData: BaseReportData,
  entityId: string,
  activitiesSectorId: string,
  activitiesGroupId: string
) {
  const exportActivityTypeMap: Map<string, ExportActivityTypeData> = new Map();
  const userActivityTypes = baseData.userGroupData.activityTypes ?? [];

  for (const activityType of userActivityTypes) {
    const activityTypeData = baseData.groupActivitiesTypesData.find(
      (t) => t.id === activityType.activityTypeId
    );

    if (!activityTypeData) {
      console.warn(
        `Activity Type with id '${activityType.activityTypeId}' not found for activity type with activityTypeId '${activityType.activityTypeId}'`
      );
      continue;
    }

    exportActivityTypeMap.set(activityType.id, {
      activityType: activityTypeData,
      // @ts-ignore (hack *)
      config: EntityActivitiesRepository.instance.getTypeCarePlanConfigurations(
        entityId,
        activitiesSectorId,
        activitiesGroupId,
        activityType.activityTypeId
      ),
      configValues: activityType.configurations,
    });
  }
  // Hack *: handle all promises at ONCE
  const exportActivityTypesData = Array.from(exportActivityTypeMap.values());
  await Promise.all(
    exportActivityTypesData.map(async (value) => {
      value.config = await value.config;
    })
  );

  // Get additional processing if needed for edge cases
  await processAdditionalActivityTypeData(baseData, exportActivityTypesData);

  return exportActivityTypeMap;
}

// 3. Process Group Details
function processUserGroupDetailsData(baseData: BaseReportData): ExportGroupDetailsData[] {
  const userCarePlanGroupDetails = baseData.userGroupData.configurations ?? [];
  const userGroupDetailsData: ExportGroupDetailsData[] = [];

  for (const detail of userCarePlanGroupDetails) {
    const config = baseData.groupDetailsData.find((c) => c.id === detail.carePlanConfigurationId);

    if (!config) {
      console.warn(
        `Configuration with id ${detail.carePlanConfigurationId} not found for detail with carePlanConfigurationId ${detail.carePlanConfigurationId}`
      );
      continue;
    }

    userGroupDetailsData.push({
      config: config,
      configValues: config.multipleValues
        ? (detail.carePlanConfigurationValue?.map((item: string) => {
            return config.arrayItems?.find((i) => i.id === item) ?? item;
          }) ?? undefined)
        : (config.arrayItems?.find((item) => item.id === detail.carePlanConfigurationValue) ??
          detail.carePlanConfigurationValue ??
          undefined),
    });
  }

  return userGroupDetailsData;
}
